import { useEffect, useMemo, useState } from "react";
import type { AdminSession, PaymentReceiptItem, PaymentVoidItem, PaymentVoidPreview } from "@/lib/api";
import { getPaymentVoidPreview, listPaymentVoids, searchPayments, voidPayment } from "@/lib/api";
import {
  buildPaymentVoidReceiptHtml,
  downloadReceiptHtml,
  formatDocumentLabel,
  openPrintWindow,
} from "@/lib/receipt";

type Props = {
  open: boolean;
  onClose: () => void;
  session: AdminSession | null;
};

type ModalTab = "void" | "history";

function todayLocal(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatMoney(value: number): string {
  return `$${value.toLocaleString("es-CL")}`;
}

export default function PaymentVoidModal({ open, onClose, session }: Props) {
  const [tab, setTab] = useState<ModalTab>("void");
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState<PaymentReceiptItem[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<PaymentReceiptItem | null>(null);
  const [preview, setPreview] = useState<PaymentVoidPreview | null>(null);
  const [reason, setReason] = useState("");
  const [historyDate, setHistoryDate] = useState(todayLocal());
  const [history, setHistory] = useState<PaymentVoidItem[]>([]);
  const [lastVoid, setLastVoid] = useState<PaymentVoidItem | null>(null);

  const selectedTotal = useMemo(
    () => preview?.documents.reduce((acc, item) => acc + item.allocatedAmount, 0) ?? 0,
    [preview],
  );

  async function handleSearch(): Promise<void> {
    if (!session) return;
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      return;
    }
    setSearching(true);
    setError("");
    try {
      const rows = await searchPayments(session, trimmed);
      setResults(rows);
      if (rows.length === 0) {
        setError("No se encontraron pagos para anular.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo buscar pagos");
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  async function handleSelectPayment(payment: PaymentReceiptItem): Promise<void> {
    if (!session) return;
    setSelectedPayment(payment);
    setPreview(null);
    setError("");
    setLoadingPreview(true);
    try {
      const response = await getPaymentVoidPreview(session, payment.id);
      setPreview(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cargar la vista previa");
    } finally {
      setLoadingPreview(false);
    }
  }

  async function loadHistory(date = historyDate): Promise<void> {
    if (!session) return;
    setHistoryLoading(true);
    setError("");
    try {
      const rows = await listPaymentVoids(session, { date, limit: 30 });
      setHistory(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cargar historial");
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  useEffect(() => {
    if (!open || !session) return;
    void loadHistory(historyDate);
  }, [open, session, historyDate]);

  useEffect(() => {
    if (!open) return;
    setError("");
  }, [open, tab]);

  if (!open) return null;

  function buildVoidReceipt(item: PaymentVoidItem): string {
    const rows = (item.snapshot.allocations ?? []).map((allocation) => ({
      label: formatDocumentLabel(
        allocation.billingDocument?.documentType ?? "",
        allocation.billingDocument?.folio ?? null,
        allocation.billingDocument?.id ?? allocation.billingDocumentId ?? "",
      ),
      dueDate: allocation.billingDocument?.dueDate ?? item.paidAtOriginal,
      amount: Number(allocation.allocatedAmount ?? 0),
    }));
    return buildPaymentVoidReceiptHtml({
      voidCode: item.voidCode,
      receiptNumber: item.receiptNumber,
      paidAtOriginal: item.paidAtOriginal,
      voidedAt: item.voidedAt,
      voidedByName: item.voidedByName,
      reason: item.reason,
      customerName: item.snapshot.customer?.fullName ?? "Cliente",
      customerRut: item.snapshot.customer?.rut ?? "-",
      contractNumber: item.snapshot.contract?.contractNumber ?? "-",
      paymentMethodName: item.paymentMethodName,
      rows,
      total: item.amount,
    });
  }

  function handlePrintVoid(item: PaymentVoidItem): void {
    openPrintWindow(buildVoidReceipt(item));
  }

  function handleDownloadVoid(item: PaymentVoidItem): void {
    downloadReceiptHtml(buildVoidReceipt(item), item.voidCode);
  }

  async function handleConfirmVoid(): Promise<void> {
    if (!session || !selectedPayment || !preview) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setError("Debes ingresar un motivo de anulacion.");
      return;
    }
    if (!preview.canVoid) {
      setError(preview.blockingReason ?? "El pago no puede anularse.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const response = await voidPayment(session, selectedPayment.id, { reason: trimmedReason });
      setLastVoid(response.void);
      setPreview(null);
      setSelectedPayment(null);
      setResults([]);
      setQuery("");
      setReason("");
      await loadHistory(historyDate);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo anular el pago");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/45 p-4">
      <div className="mx-auto flex h-full w-full max-w-6xl items-center justify-center">
        <div className="flex max-h-[92vh] w-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Anulacion de pagos</h2>
              <p className="text-sm text-slate-500">Revierte pagos registrados por error y libera nuevamente el documento.</p>
            </div>
            <button
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-6 py-4">
            <div className="flex items-center gap-2 rounded-2xl bg-slate-100 p-1">
              <button
                onClick={() => setTab("void")}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition ${tab === "void" ? "bg-white text-amber-700 shadow-sm" : "text-slate-500"}`}
              >
                Anular pago
              </button>
              <button
                onClick={() => setTab("history")}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition ${tab === "history" ? "bg-white text-amber-700 shadow-sm" : "text-slate-500"}`}
              >
                Historial
              </button>
            </div>

            {tab === "history" ? (
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-slate-600">Fecha</label>
                <input
                  type="date"
                  value={historyDate}
                  onChange={(e) => setHistoryDate(e.target.value)}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
            ) : null}
          </div>

          {error ? (
            <div className="mx-6 mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {tab === "void" ? (
              <div className="space-y-5">
                {lastVoid ? (
                  <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <h3 className="text-base font-semibold text-emerald-800">Pago anulado correctamente</h3>
                        <p className="mt-1 text-sm text-emerald-700">
                          {lastVoid.voidCode} - comprobante {lastVoid.receiptNumber}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handlePrintVoid(lastVoid)}
                          className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100"
                        >
                          <i className="ri-printer-line mr-1.5" />Imprimir comprobante
                        </button>
                        <button
                          onClick={() => handleDownloadVoid(lastVoid)}
                          className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                        >
                          <i className="ri-download-line mr-1.5" />Descargar
                        </button>
                        <button
                          onClick={() => setLastVoid(null)}
                          className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                        >
                          Nueva anulacion
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {!lastVoid ? (
                  <>
                    <div className="rounded-3xl border border-slate-200 bg-white p-5">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") void handleSearch(); }}
                          placeholder="Buscar por comprobante, contrato, RUT o cliente"
                          className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-amber-400"
                        />
                        <button
                          onClick={() => void handleSearch()}
                          disabled={searching}
                          className="rounded-2xl bg-amber-600 px-4 py-3 text-sm font-medium text-white transition hover:bg-amber-700 disabled:opacity-60"
                        >
                          {searching ? "Buscando..." : "Buscar"}
                        </button>
                      </div>
                    </div>

                    <div className="grid gap-5 xl:grid-cols-[0.95fr_1.05fr]">
                      <div className="rounded-3xl border border-slate-200 bg-white">
                        <div className="border-b border-slate-100 px-5 py-4">
                          <h3 className="text-sm font-semibold text-slate-800">Pagos encontrados</h3>
                        </div>
                        <div className="max-h-[28rem] overflow-auto p-4">
                          {results.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-400">
                              Busca un pago activo para cargar la anulacion.
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {results.map((payment) => (
                                <button
                                  key={payment.id}
                                  onClick={() => void handleSelectPayment(payment)}
                                  className={`w-full rounded-2xl border px-4 py-3 text-left transition ${selectedPayment?.id === payment.id ? "border-amber-300 bg-amber-50" : "border-slate-200 hover:border-amber-200 hover:bg-amber-50/40"}`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="text-sm font-semibold text-slate-800">{payment.contract.customer.fullName}</div>
                                      <div className="text-xs text-slate-500">
                                        Contrato {payment.contract.contractNumber} - {payment.receiptNumber}
                                      </div>
                                      <div className="text-xs text-slate-400">
                                        {new Date(payment.paidAt).toLocaleString("es-CL")}
                                        {payment.paymentMethod ? ` - ${payment.paymentMethod.name}` : ""}
                                      </div>
                                    </div>
                                    <div className="shrink-0 text-sm font-bold text-slate-800">{formatMoney(payment.amount)}</div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="rounded-3xl border border-slate-200 bg-white">
                        <div className="border-b border-slate-100 px-5 py-4">
                          <h3 className="text-sm font-semibold text-slate-800">Vista previa</h3>
                        </div>
                        <div className="p-5">
                          {loadingPreview ? (
                            <div className="flex h-52 items-center justify-center text-sm text-slate-500">Cargando vista previa...</div>
                          ) : null}

                          {!loadingPreview && !preview ? (
                            <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-400">
                              Selecciona un pago para ver el impacto antes de anular.
                            </div>
                          ) : null}

                          {!loadingPreview && preview ? (
                            <div className="space-y-5">
                              <div className="grid gap-3 md:grid-cols-2">
                                <div className="rounded-2xl bg-slate-50 p-4">
                                  <div className="text-xs uppercase tracking-wide text-slate-400">Comprobante</div>
                                  <div className="mt-2 text-lg font-bold text-slate-800">{preview.payment.receiptNumber}</div>
                                  <div className="mt-1 text-xs text-slate-500">{new Date(preview.payment.paidAt).toLocaleString("es-CL")}</div>
                                </div>
                                <div className="rounded-2xl bg-slate-50 p-4">
                                  <div className="text-xs uppercase tracking-wide text-slate-400">Monto</div>
                                  <div className="mt-2 text-lg font-bold text-slate-800">{formatMoney(preview.payment.amount)}</div>
                                  <div className="mt-1 text-xs text-slate-500">{preview.payment.paymentMethod.name}</div>
                                </div>
                              </div>

                              <div className="rounded-2xl border border-slate-200 p-4">
                                <div className="text-sm font-semibold text-slate-800">{preview.payment.contract.customerName}</div>
                                <div className="mt-1 text-xs text-slate-500">
                                  {preview.payment.contract.customerRut} - Contrato {preview.payment.contract.contractNumber}
                                </div>
                                {preview.payment.reference ? (
                                  <div className="mt-2 text-xs text-slate-500">Referencia: {preview.payment.reference}</div>
                                ) : null}
                              </div>

                              {preview.blockingReason ? (
                                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                  <i className="ri-alert-line mr-1.5" />{preview.blockingReason}
                                </div>
                              ) : null}

                              <div className="grid gap-3 md:grid-cols-2">
                                <div className="rounded-2xl border border-slate-200 p-4">
                                  <div className="text-xs uppercase tracking-wide text-slate-400">Saldo actual</div>
                                  <div className="mt-2 text-sm font-semibold text-slate-800">
                                    Corriente: {formatMoney(preview.contractBalance.current.currentBalance)}
                                  </div>
                                  <div className="mt-1 text-sm font-semibold text-slate-800">
                                    Vencido: {formatMoney(preview.contractBalance.current.overdueBalance)}
                                  </div>
                                </div>
                                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                                  <div className="text-xs uppercase tracking-wide text-amber-600">Saldo proyectado</div>
                                  <div className="mt-2 text-sm font-semibold text-amber-900">
                                    Corriente: {formatMoney(preview.contractBalance.projected.currentBalance)}
                                  </div>
                                  <div className="mt-1 text-sm font-semibold text-amber-900">
                                    Vencido: {formatMoney(preview.contractBalance.projected.overdueBalance)}
                                  </div>
                                </div>
                              </div>

                              <div className="rounded-2xl border border-slate-200">
                                <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">
                                  Documentos afectados
                                </div>
                                <div className="divide-y divide-slate-100">
                                  {preview.documents.map((item) => (
                                    <div key={item.billingDocumentId} className="px-4 py-3">
                                      <div className="flex items-start justify-between gap-3">
                                        <div>
                                          <div className="text-sm font-medium text-slate-800">
                                            {formatDocumentLabel(item.documentType, item.folio, item.billingDocumentId)}
                                          </div>
                                          <div className="mt-1 text-xs text-slate-500">
                                            Estado actual: {item.currentStatus} - Estado despues: {item.nextStatus}
                                          </div>
                                          <div className="mt-1 text-xs text-slate-400">
                                            Vence {new Date(item.dueDate).toLocaleDateString("es-CL")}
                                          </div>
                                        </div>
                                        <div className="text-right">
                                          <div className="text-sm font-semibold text-slate-800">{formatMoney(item.allocatedAmount)}</div>
                                          <div className="mt-1 text-xs text-slate-500">
                                            Pendiente: {formatMoney(item.outstandingAfterVoid)}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div>
                                <label className="mb-2 block text-xs font-semibold text-slate-600">Motivo de anulacion</label>
                                <textarea
                                  value={reason}
                                  onChange={(e) => setReason(e.target.value)}
                                  rows={4}
                                  placeholder="Describe el error administrativo que obliga a revertir el pago"
                                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-amber-400"
                                />
                              </div>

                              <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-500">
                                  Total a liberar: <span className="font-semibold text-slate-700">{formatMoney(selectedTotal)}</span>
                                </div>
                                <button
                                  onClick={() => void handleConfirmVoid()}
                                  disabled={submitting || !preview.canVoid}
                                  className="rounded-xl bg-amber-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-amber-700 disabled:opacity-60"
                                >
                                  {submitting ? "Anulando..." : "Confirmar anulacion"}
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}

            {tab === "history" ? (
              <div className="space-y-4">
                {historyLoading ? (
                  <div className="flex h-56 items-center justify-center text-sm text-slate-500">Cargando historial...</div>
                ) : null}

                {!historyLoading && history.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-slate-200 px-6 py-12 text-center text-sm text-slate-400">
                    No hay anulaciones registradas para esta fecha.
                  </div>
                ) : null}

                {!historyLoading && history.length > 0 ? (
                  history.map((item) => (
                    <div key={item.id} className="rounded-3xl border border-slate-200 bg-white p-5">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold text-slate-800">{item.voidCode}</h3>
                            <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">
                              {item.receiptNumber}
                            </span>
                          </div>
                          <div className="mt-1 text-sm text-slate-500">
                            {item.snapshot.customer?.fullName ?? "Cliente"} - Contrato {item.snapshot.contract?.contractNumber ?? "-"}
                          </div>
                          <div className="mt-1 text-xs text-slate-400">
                            Pago original {new Date(item.paidAtOriginal).toLocaleString("es-CL")} - Anulado {new Date(item.voidedAt).toLocaleString("es-CL")}
                          </div>
                          <div className="mt-2 text-sm text-slate-600">{item.reason}</div>
                          {item.voidedByName ? (
                            <div className="mt-1 text-xs text-slate-400">Usuario: {item.voidedByName}</div>
                          ) : null}
                        </div>

                        <div className="grid gap-2 text-right sm:grid-cols-3 lg:min-w-[320px]">
                          <div className="rounded-2xl bg-slate-50 px-4 py-3">
                            <div className="text-xs text-slate-400">Monto</div>
                            <div className="text-sm font-bold text-slate-800">{formatMoney(item.amount)}</div>
                          </div>
                          <div className="rounded-2xl bg-slate-50 px-4 py-3">
                            <div className="text-xs text-slate-400">Saldo antes</div>
                            <div className="text-sm font-bold text-slate-800">
                              {formatMoney(item.snapshot.contractBalanceBefore?.currentBalance ?? 0)}
                            </div>
                          </div>
                          <div className="rounded-2xl bg-amber-50 px-4 py-3">
                            <div className="text-xs text-amber-600">Saldo despues</div>
                            <div className="text-sm font-bold text-amber-800">
                              {formatMoney(item.snapshot.contractBalanceAfter?.currentBalance ?? 0)}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          onClick={() => handlePrintVoid(item)}
                          className="rounded-xl bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700 transition hover:bg-amber-100"
                        >
                          <i className="ri-printer-line mr-1.5" />Imprimir
                        </button>
                        <button
                          onClick={() => handleDownloadVoid(item)}
                          className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
                        >
                          <i className="ri-download-line mr-1.5" />Descargar
                        </button>
                      </div>
                    </div>
                  ))
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
